import Head from "next/head";
import Link from "next/link";
import NextScript from "next/script";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import type { TRegistrationCardData } from "src/shared/types/globalStotre.types";

export const REGISTRATION_CARDS: TRegistrationCardData[] = [
  {
    title: "First Match On Us",
    description:
      "Login with the same email used on your form submission to pick your first match.",
    authRedirectPage: routes.home,
    buttonLabel: "Pick Your Match",
    buttonVariant: "contained",
    primaryButtonStyles: {
      backgroundColor: "#00FF61",
      border: "2px solid #000000",
      color: "#000000",
      ":hover": {
        backgroundColor: "#00FF61",
        border: "2px solid #000000",
        color: "#000000",
      },
    },
    titleStyles: {
      color: "#000000",
    },
  },
];

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How do I claim my tickets?",
    answer:
      "You will receive a text when the window to check in is opened. The check in window starts at 9:00 AM the day before the homestand begins. After checking in you will have the opportunity to purchase guest tickets & upgrades if they are available.",
  },
  {
    question: "Do I get a ticket to each game on the schedule?",
    answer:
      "Tickets are guaranteed for the games defined in the pass. There are no penalties for not attending a game, however you do need to confirm your attendance in order to receive a ticket. Please note that your League Sounders First Match on Us only guarantees you tickets to games you have checked in for.",
  },
  {
    question: "How are tickets sent to me?",
    answer:
      "3 hours prior to game start you will receive a text confirming that tickets have been delivered to your account.",
  },
  {
    question: "Where will my seats be located?",
    answer: "Seat locations will vary by game.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Yes, the Passes are non-transferable, but can be assigned to others in your party to enter separately.",
  },
  {
    question: "Can my seats be changed?",
    answer: (
      <>
        Once tickets have been sent to your account, they cannot be changed.
        Please contact{" "}
        <a href="mailto:ticketsupport@soundersfc.com">
          ticketsupport@soundersfc.com
        </a>{" "}
        for any assistance you may require.
      </>
    ),
  },
  {
    question: "Can I link tickets with other pass holders?",
    answer: (
      <>
        You can absolutely create a group to link with other Sounders First
        Match on Us holders! Navigate to the{" "}
        <Link
          href={{
            pathname: routes.my_group,
            query: { teamSchema: "seattlesounders_pass" },
          }}
        >
          My Group
        </Link>{" "}
        tab in your menu and copy the invite. This will ensure you are seated
        next to or close enough to hand a drink to one another for the game. You
        will need to ensure your group is created and all members have joined at
        least 48 hours prior to game start in order to be seated with one
        another.
      </>
    ),
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
];

export const REGISTER_TERMS_AND_CONDITION = () => (
  <>
    By checking this box, you agree to receive recurring automated marketing
    text messages (e.g. team/account information & ticket offers) sent by Attend
    & Attentive on Sounders FC&apos;s behalf, at the number you provide. Consent
    not a condition of purchase. We may share info with service providers per
    our Privacy Policy.{" "}
    <Link href="https://attnl.tv/t/O2l/r/US" target="_blank">
      Terms
    </Link>{" "}
    (incl. arbitration) &{" "}
    <Link href="https://attnl.tv/p/O2l" target="_blank">
      Privacy Policy
    </Link>
    .
  </>
);

export const TERMS_AND_CONDITION = () => (
  <>
    By checking the box you agree to receive notifications regarding your
    purchase and to the{" "}
    <Link target="_blank" href="https://www.attend.tech/terms-of-use">
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link target="_blank" href="https://www.attend.tech/privacy-policy">
      Privacy Policy
    </Link>
    . These tickets are not eligible for resale.
  </>
);

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '493354401187639');
            fbq('track', 'PageView');
            window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
              try{
                const { cartRes, checkoutRes, passes } = e.detail;

                let content_name = "";
                if(cartRes.products && cartRes.products.length) {
                  content_name = "${TEAM_SCHEMA}_single_game_purchases";
                };
                if(cartRes.passes && cartRes.passes.length && passes.length ) {
                  const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                  content_name = passDetail.passTitle;
                };

                fbq('track', 'Purchase', {
                  content_name: content_name, 
                  value: String(cartRes.totalAmount), 
                  currency: 'USD', 
                  order_id: checkoutRes.orderNumber,
                });
                console.log('meta pixel purchase event fired.');
              
              } catch(error){
                console.log('meta pixel purchase event error:', error); 
              }
            });
          `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=493354401187639&ev=PageView&noscript=1"
        />
      </noscript>

      <NextScript
        async
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        src="https://www.googletagmanager.com/gtag/js?id=DC-15814684"
      />
      <script
        id={`${TEAM_SCHEMA}-floodlight-inline`}
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag() {
              dataLayer.push(arguments);
            }
            gtag('js', new Date());
            gtag('config', 'DC-15814684');

            try {
              gtag('event', 'conversion', {
                allow_custom_scripts: true,
                send_to: 'DC-15814684/ssfc/seatt0+standard'
              });
              console.log('floodlight RTG event fired.');
            } catch (error) {
              console.log('floodlight RTG event error:', error);
            }

            window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
              try {
                const { cartRes, checkoutRes } = e.detail;
                gtag('event', 'purchase', {
                  allow_custom_scripts: true,
                  value: cartRes.totalAmount,
                  transaction_id: checkoutRes.orderNumber,
                  send_to: 'DC-15814684/ssfcsale/seatt00+transactions'
                });
                console.log('floodlight purchase event fired.');
              } catch (error) {
                console.log('floodlight purchase event error:', error);
              }
            });
          `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://ad.doubleclick.net/ddm/activity/src=15814684;type=ssfc;cat=seatt0;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;npa=;gdpr=;gdpr_consent=;ord=1?"
          alt=""
        />
      </noscript>
    </Head>
  );
};
